import React, { useState } from "react";
import { Form, Select, Table, Button, Input, Space, Image, Row, Col, Spin, message, Flex } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { HotelSelect } from "../common/HotelSelect";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../store/Hooks";
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import { OtherAgencyService } from "../../services/OtherAgencyService";
import ManyOtherAgency from "../../assets/img/ManyOtherAgency.jpg";
import ManyOtherAgency_en from "../../assets/img/ManyOtherAgency_en.jpg";
import { ObjectHelper } from "../../utils/ObjectHelper";

export type AddMultipleOtherAgencyProps = {
    callback?: Function;
};

const AddMultipleOtherAgency = ({ callback }: AddMultipleOtherAgencyProps) => {
    const [selectedHotels, setSelectedHotels] = useState<string[]>([]);
    const [otherAgencies, setOtherAgencies] = useState<string>("");
    const [agencyList, setAgencyList] = useState<{ agencyName: string; email: string }[]>([]);

    const language = useAppSelector((state) => state.site.language);
    const { t } = useTranslation();

    const mutation = useMutation(OtherAgencyService.SaveOtherAgencyList, {
        onSuccess: (response) => {
            if (response.body?.isSuccessful) {
                toast.success(t("common.success"));
                resetForm();
                callback?.();
            } else {
                toast.error(response.body?.message || ObjectHelper.getErrorMessage(response.header.messages));
            }
        },
        onError: () => {
            toast.error(t("definitions.otherAgencies.addMultipleOtherAgenciesForm.saveError"));
        },
    });

    const resetForm = () => {
        setSelectedHotels([]);
        setAgencyList([]);
        setOtherAgencies("");
    };

    const handleTextareaChange = (value: string) => {
        setOtherAgencies(value);
        const rows = value.split("\n").filter((row, index) => index > 0 && row.trim());

        const updatedAgencies = rows
            .map((row) => {
                const [agencyName, email] = row.split("\t").map((item) => item.trim());
                if (!agencyName || !email) return null;
                return { agencyName, email };
            })
            .filter(Boolean) as { agencyName: string; email: string }[];

        const uniqueAgencies = Array.from(
            new Map(updatedAgencies.map((agency) => [agency.email, agency])).values()
        );

        setAgencyList(uniqueAgencies);
    };

    const handleDeleteAgency = (email: string) => {
        setAgencyList((prev) => prev.filter((agency) => agency.email !== email));
        const updatedTextarea = otherAgencies
            .split("\n")
            .filter((row) => !row.includes(email))
            .join("\n");
        setOtherAgencies(updatedTextarea);
        toast.success(t("definitions.otherAgencies.addMultipleOtherAgenciesForm.agencyDeletedSuccessfully"));
    };

    const handleHotelChange = (hotels: { id: string }[]) => {
        setSelectedHotels(hotels.map((hotel) => hotel.id));
    };

    const handleSubmit = () => {
        if (!selectedHotels.length) {
            toast.error(
                t("definitions.otherAgencies.addMultipleOtherAgenciesForm.selectAtLeastOneHotel")
            );
            return;
        }

        if (!agencyList.length) {
            toast.error(
                t("definitions.otherAgencies.addMultipleOtherAgenciesForm.agencyListCannotBeEmpty")
            );
            return;
        }

        const request = {
            hotelIds: selectedHotels,
            otherAgencyList: agencyList,
        };

        mutation.mutate(request);
    };


    const columns = [
        {
            title: t("definitions.otherAgencies.addMultipleOtherAgenciesForm.agency"),
            dataIndex: "agencyName",
            key: "agencyName",
        },
        {
            title: t("definitions.otherAgencies.addMultipleOtherAgenciesForm.email"),
            dataIndex: "email",
            key: "email",
        },
        {
            title: t("definitions.otherAgencies.addMultipleOtherAgenciesForm.actions"),
            key: "action",
            render: (_: any, record: { email: string }) => (
                <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteAgency(record.email)}
                >
                    {t("definitions.otherAgencies.addMultipleOtherAgenciesForm.delete")}
                </Button>
            ),
        },
    ];

    return (
        <Spin spinning={mutation.isLoading}>
            <Form layout="vertical" id="AddMultipleOtherAgency" onFinish={handleSubmit}>
                <Form.Item label={t("definitions.otherAgencies.hotels")} required>
                    <HotelSelect sendObject={true} mode={"multiple"} onChange={handleHotelChange} />
                </Form.Item>

                <Form.Item label={t("definitions.otherAgencies.addMultipleOtherAgenciesForm.info")}>
                    <Row>
                        <Col style={{ width: "50%" }}>
                            <Image src={language === "en-US" ? ManyOtherAgency_en : ManyOtherAgency} alt="Example" />
                        </Col>
                        <Col style={{ width: "50%" }}>
                            <Input.TextArea
                                rows={8}
                                value={otherAgencies}
                                onChange={(e) => handleTextareaChange(e.target.value)}
                                placeholder={t("definitions.otherAgencies.addMultipleOtherAgenciesForm.pasteInAgencyNameEmailFormat")}
                            />
                        </Col>
                    </Row>
                </Form.Item>

                <Form.Item>
                    {agencyList.length > 0 && (
                        <Table
                            columns={columns}
                            dataSource={agencyList}
                            rowKey="email"
                            pagination={false}
                            bordered
                        />
                    )}
                </Form.Item>

                <Form.Item>
                    <Flex justify="end">
                        <Button type="primary" htmlType="submit">
                            {t("definitions.otherAgencies.addMultipleOtherAgenciesForm.save")}
                        </Button>
                    </Flex>
                </Form.Item>
            </Form>
        </Spin>
    );
};

export default AddMultipleOtherAgency;
